import React, { useEffect, useState, createContext, useContext } from "react"
import { ListRfidInfo, RfidInfo } from "types/rfid"
import { useMmsService } from "services/mms/context/MmsServiceContext.tsx"
import {AccountInfo, ListAccountInfo} from "types/list-card-holder"
import Button from "@mui/material/Button"
import { Step1 } from "layouts/rfid/rfidActivationFormStep-1.tsx"
import { Step2 } from "layouts/rfid/rfidActivationFormStep-2.tsx"
import { Step3 } from "layouts/rfid/rfidActivationFormStep-3.tsx"
import { ValidationStep } from "layouts/rfid/rfidActivationFormValidation.tsx"

import ErrorIcon from "assets/svg/error-icon.svg"
import { OrevePopup, OrevePopupTitle } from "components/popup/OrevePopup.tsx"
import { OreveButton } from "components/button/OreveButton.tsx"
import { Typography, Box } from "@mui/material"
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import { noRulesApplied, RfidStatus } from "layouts/rfid/utils/const.ts"
import { BackendSchema } from "layouts/charging-rules/interface/Rules.ts"

enum RfidActivationStep {
    CHOSING_RFID_CARD,
    CHOSING_DRIVER,
    CHOSING_RULE,
    VALIDATION
}

interface IRfidActivationFormContext {
    chosenCard?: RfidInfo,
    chosenAccount: AccountInfo | null,
    activeAccountList?: ListAccountInfo,
    fetchDrivers: () => void,
    setChosenAccount: React.Dispatch<React.SetStateAction<AccountInfo | null>>
}

interface RfidActivationFormProps {
    RfidCardSource: ListRfidInfo;
    goBackToList: () => void
}

// eslint-disable-next-line react-refresh/only-export-components
export const RfidActivationFormContext = createContext<IRfidActivationFormContext | undefined>(undefined)

export const useActivationFormContext = () => {
    const context = useContext(RfidActivationFormContext)
    if (context === undefined) {
        throw new Error(
            "useActivationFormContext doit être utilisé à l'intérieur de RfidActivationFormContext"
        )
    }
    return context
}


export const RfidActivationForm: React.FC<RfidActivationFormProps> = ({RfidCardSource, goBackToList}) => {
    const [chosenCard, setChosenCard] = useState<RfidInfo | undefined>(undefined)
    const [activeAccountList, setAccountList] = useState<ListAccountInfo | undefined>(undefined)
    const [currentStep, setCurrentStep] = useState<RfidActivationStep>(0)
    const [chosenAccount, setChosenAccount] = useState<AccountInfo | null>(null)
    const [popupDisplay, setPopupDisplay] = useState<boolean>(false)
    const [formIsFinished, setFormIsFinished] = useState<boolean>(false)
    const [ruleRfidCard, setRuleRfidCard] = useState<BackendSchema>(noRulesApplied)

    const { cardHolderService, rfidService, rfidRuleService } = useMmsService()
    const { useGetListCardHolders } = cardHolderService
    const { useGetDriverRfidsCards } = rfidService
    const { useUpdateRfidCardRule } = rfidRuleService
    const { data: rawAccountList, sendRequest: fetchListDrivers, success: listCardHolderStatus } = useGetListCardHolders()
    const { data: driverCards, sendRequest: fetchDriverCards, success: driverCardsStatus } = useGetDriverRfidsCards(chosenAccount?.info.id ?? "")
    const { sendRequest: updateRfidCardRule, success: updateRfidCardRuleStatus  } = useUpdateRfidCardRule(chosenCard?.id ?? "", ruleRfidCard)


    useEffect(() => {
        setCurrentStep(RfidActivationStep.CHOSING_RFID_CARD)
    }, [])

    useEffect(() => {
        if(formIsFinished) {
            updateRfidCardRule().then(() => fetchDriverCards())
            setCurrentStep(RfidActivationStep.VALIDATION)
            setFormIsFinished(false)
        }
    }, [formIsFinished])

    function validateCard(card: RfidInfo){
        setChosenCard(card)
        setCurrentStep(RfidActivationStep.CHOSING_DRIVER)
    }

    function validateDriver() {
        setCurrentStep(RfidActivationStep.CHOSING_RULE)
    }

    function fetchDrivers() {
        fetchListDrivers().then()
    }

    function checkDriverCard(cardList: RfidInfo[]) {
        if (cardList.length == 0) {
            setCurrentStep(RfidActivationStep.VALIDATION)
        } else {
            setPopupDisplay(true)
        }
    }

    useEffect(() => {
        if (driverCards) checkDriverCard(driverCards as RfidInfo[])
    }, [driverCards])

    useEffect(() => {
        if (rawAccountList) setAccountList((rawAccountList as ListAccountInfo).filter((account) => account.info.active))
    }, [rawAccountList])

    function validateProcess() {
        setFormIsFinished(true)
    }

    function PageSwitch(step: RfidActivationStep) {
        switch (step) {
            case RfidActivationStep.CHOSING_RFID_CARD:
                return <Step1
                    inactiveRfidCardSource={RfidCardSource.filter((card) => card.status === RfidStatus.ToBeActivate)}
                    validateCard={validateCard}
                    goBackToList={goBackToList}
                />
            case RfidActivationStep.CHOSING_DRIVER:
                return (
                    <RfidActivationFormContext.Provider value={{chosenAccount, activeAccountList, fetchDrivers, setChosenAccount}}>
                        <Step2
                            goBackToList={goBackToList}
                            validateDriver={() => validateDriver()}
                        />
                        <OrevePopup open={popupDisplay} handleClose={() => setPopupDisplay(false)}>
                           <OrevePopupTitle>
                               <img src={ErrorIcon} alt={"error-icon"} />
                               <Typography
                                   display="block"
                                   sx={{
                                       fontSize: '1.5rem',
                                       fontWeight: 400,
                                       lineHeight: '2rem',
                                       letterSpacing: '0em',
                                       textAlign: 'center',
                                   }}
                                   color="error"
                               >
                                   {chosenAccount?.type === 'vehicle' ?
                                       "Le véhicule " + chosenAccount?.info.licensePlate : "Le conducteur " + chosenAccount?.info.name
                                   } est déjà associé à une autre carte
                               </Typography>
                           </OrevePopupTitle>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: "1.5rem",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <OreveButton
                                    icon={ArrowForwardIcon}
                                    value="Poursuivre l'activation"
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => setCurrentStep(RfidActivationStep.CHOSING_RULE)}
                                    sx={{ mt: "1.5rem" }}
                                />
                                <OreveButton
                                    icon={BorderColorIcon}
                                    value="Modifier le compte"
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setPopupDisplay(false)}
                                    sx={{ mt: "1.5rem" }}
                                />
                            </Box>
                        </OrevePopup>
                    </RfidActivationFormContext.Provider>
                )
            case RfidActivationStep.CHOSING_RULE:
                return <Step3
                    goBackToList={goBackToList}
                    setRuleRfidCard={setRuleRfidCard}
                    validateRule={validateProcess}
                />
            case RfidActivationStep.VALIDATION:
                return <RfidActivationFormContext.Provider value={{chosenCard, chosenAccount, activeAccountList, fetchDrivers, setChosenAccount}}>
                    <ValidationStep
                        goBackToList={goBackToList}
                        rfidActivationSuccess={(updateRfidCardRuleStatus && listCardHolderStatus && driverCardsStatus)}
                    />
                    </RfidActivationFormContext.Provider>
            default:
                return <Button></Button>
        }
    }

    return PageSwitch(currentStep)
}
